import {
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableBody,
  TableRow,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { useContext, useState, useEffect, useCallback } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { AlternateAliasContext } from "../../context/AlternateAliasContext";

const WSU_RED = "#A60F2D";

const columns = [
  { id: "displayName",       label: "Display Name", minWidth: 150 },
  { id: "userPrincipalName", label: "UPN",          minWidth: 150 },
  { id: "mail",              label: "Email",         minWidth: 150 },
];

export const AlternateAliasSearchResultsTable = ({ text }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [state, setState] = useState({ data: [], isLoaded: false, error: null });

  const { authState } = useOktaAuth();
  const { setAliasModule } = useContext(AlternateAliasContext);

  const fetchData = useCallback(async (searchText, token) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);

    try {
      const response = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/alias/search`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: token,
            },
            body: JSON.stringify({ searchValue: searchText.trim() }),
            signal: controller.signal,
          }
      );

      if (!response.ok) {
        const message =
            response.status === 504
                ? "The request timed out. Please refine your search or try again later."
                : (await response.json().catch(() => ({}))).error?.message ||
                "An unknown error occurred.";
        throw new Error(message);
      }

      const result = await response.json();
      const formatted = (result.results || []).map((alias) => ({
        dn: alias.dn,
        displayName: alias.displayName,
        userPrincipalName: alias.userPrincipalName,
        mail: alias.mail,
      }));

      setState({ isLoaded: true, data: formatted, error: null });
      setAliasModule((prev) => ({ ...prev, searchResults: formatted }));
    } catch (err) {
      const errorMessage =
          err.name === "AbortError"
              ? "The request timed out. Please refine your search or try again later."
              : err.name === "TypeError"
                  ? "Network error or request was blocked. Please try again."
                  : err.message;

      setState({ isLoaded: true, data: [], error: new Error(errorMessage) });
      setAliasModule((prev) => ({ ...prev, searchResults: [] }));
    } finally {
      clearTimeout(timeout);
    }

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [setAliasModule]);

  useEffect(() => {
    if (text && authState?.accessToken?.accessToken) {
      fetchData(text, authState.accessToken.accessToken);
    } else {
      setState({ data: [], isLoaded: true, error: null });
      setAliasModule((prev) => ({ ...prev, searchResults: [] }));
    }
  }, [text, authState.accessToken.accessToken, fetchData, setAliasModule]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (row) => {
    setAliasModule((prev) => ({
      ...prev,
      isOpen: true,
      upn: row.userPrincipalName,
      dn: row.dn,
    }));
  };

  const renderCell = (column, row) => {
    const value = row[column.id];
    return value ? (
        <Typography variant="body2">{value}</Typography>
    ) : (
        <Typography variant="body2" color="text.disabled" fontStyle="italic">—</Typography>
    );
  };

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" py={6}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.error) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Search failed
            </Typography>
            <Typography variant="body2">{state.error.message}</Typography>
          </Box>
        </Paper>
    );
  }

  if (state.data.length === 0) {
    return (
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography variant="body2" color="text.disabled" fontStyle="italic">
              No aliases found for "{text}"
            </Typography>
          </Box>
        </Paper>
    );
  }

  return (
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                    <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          minWidth: column.minWidth,
                          backgroundColor: "#4D4D4D",
                          color: "white",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          letterSpacing: 0.5,
                        }}
                    >
                      {column.label}
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {state.data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                      <TableRow
                          key={row.userPrincipalName}
                          hover
                          onClick={() => handleRowClick(row)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#fdf0f2" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                      >
                        {columns.map((column) => (
                            <TableCell key={`${row.userPrincipalName}-${column.id}`} align="center">
                              {renderCell(column, row)}
                            </TableCell>
                        ))}
                      </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={state.data.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #e0e0e0",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.8rem",
              },
            }}
        />
      </Paper>
  );
};